import React, { useCallback, useState } from 'react';
import PrimaryButton from '../components/buttons/PrimaryButton';
import Loader from '../components/Loader';

export const FileUpload = ({ onFilesUploaded }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(new Set());

  const showToast = (message, type = 'success') => {
    // Utilisation d'alert simple en attendant une solution de toast
    if (type === 'error') {
      alert(`Erreur: ${message}`);
    } else {
      alert(message);
    }
  };

  const handleFiles = useCallback(async (files) => {
    const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== files.length) {
      showToast("Veuillez uploader uniquement des fichiers PDF.", 'error');
    }

    if (pdfFiles.length === 0) return;

    const fileIds = pdfFiles.map(file => `${Date.now()}-${file.name}`);
    setUploadingFiles(new Set(fileIds));

    // Traitement immédiat des fichiers
    const newFiles = pdfFiles.map(file => ({
      name: file.name,
      size: file.size,
      id: `${Date.now()}-${file.name}`,
      file: file // Garder la référence au fichier pour l'envoi ultérieur
    }));
    
    setUploadedFiles(prev => {
      const updated = [...prev, ...newFiles];
      onFilesUploaded(updated);
      return updated;
    });
    setUploadingFiles(new Set());
  }, [onFilesUploaded]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const removeFile = (fileId) => {
    setUploadedFiles(prev => {
      const updated = prev.filter(file => file.id !== fileId);
      onFilesUploaded(updated);
      return updated;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div
        className={`transition-all duration-300 border-2 border-dashed cursor-pointer p-8 text-center rounded-lg ${
          isDragOver 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('file-upload').click()}
      >
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-blue-500 mb-4 flex items-center justify-center">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Upload de fichiers PDF</h3>
          <p className="text-gray-600 mb-4">
            Glissez-déposez vos fichiers PDF ici, ou cliquez pour parcourir
          </p>
          <input
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            id="file-upload"
            onChange={handleFileInput}
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <PrimaryButton 
              type="button"
              value="Choisir des fichiers"
              loading={false}
              bgColor="bg-blue-500 hover:bg-blue-600"
            />
          </label>
        </div>
      </div>

      {(uploadedFiles.length > 0 || uploadingFiles.size > 0) && (
        <div className="border rounded-lg p-4 bg-white">
          <h4 className="font-semibold mb-3">Fichiers uploadés</h4>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 text-blue-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="h-8 w-8 p-0 text-gray-500 hover:text-red-500 flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            {Array.from(uploadingFiles).map((fileId) => (
              <div key={fileId} className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg">
                <Loader />
                <span className="text-sm">Upload en cours...</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
